<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="top-center" />
  <p-confirmDialog />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <div class="row-start-center gap-[1rem]">
          <h1>{{ headerTitle() }}</h1>
        </div>
        <div class="page__title_buttons">
          <p-button [label]="'Back' | translateWF: 'Back'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
        </div>
      </div>
    </div>

    <div class="grid-12 gap-[1.5rem]">
      <!-- LEFT COLUMN -->
      <div class="span-12-12-8 col">
        <!-- Period -->
        <p-panel [header]="'WithholdingRemittance_Period' | translateWF: 'Period'" [toggleable]="true">
          <p-card data-variant="fileds">
            <div class="grid-12">
              <div class="span-6-4">
                <div class="form-box">
                  <label>{{ 'WithholdingRemittance_Month' | translateWF: 'Month' }}</label>
                  <p-select
                    [options]="monthOptions"
                    [ngModel]="month()"
                    (onChange)="month.set($event.value); onPeriodChange()"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="w-full"
                  />
                </div>
              </div>
              <div class="span-6-4">
                <div class="form-box">
                  <label>{{ 'WithholdingRemittance_Year' | translateWF: 'Year' }}</label>
                  <p-select
                    [options]="yearOptions"
                    [ngModel]="year()"
                    (onChange)="year.set($event.value); onPeriodChange()"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="w-full"
                  />
                </div>
              </div>
              @if (worksheet(); as w) {
                <div class="span-12-4">
                  <div class="form-box">
                    <label>{{ 'WithholdingRemittance_DueDate' | translateWF: 'Due to SAR by' }}</label>
                    <span class="rate-readout">{{ w.dueDate | date: 'dd/MM/yyyy' }}</span>
                  </div>
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        <!-- Worksheet -->
        <p-panel [header]="'WithholdingRemittance_Worksheet' | translateWF: 'Documents in this period'" [toggleable]="true">
          @if (missingCount() > 0) {
            <p-card>
              <p-message
                severity="warn"
                styleClass="w-full mb-3"
                [text]="
                  missingCount() +
                  ' ' +
                  ('WithholdingRemittance_MissingWarning' | translateWF: 'document(s) posted this period have no withholding certificate. You can still remit, but review them.')
                "
              />
            </p-card>
          }
          <p-card data-variant="compact">
            <p-table [value]="worksheet()?.rows ?? []" [tableStyle]="{ 'min-width': '48rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'ApDocument_Identity' | translateWF: 'Fiscal / Document #' }}</th>
                  <th>{{ 'ApDocument_Provider' | translateWF: 'Provider' }}</th>
                  <th>{{ 'ApDocument_PostingDate' | translateWF: 'Posted' }}</th>
                  <th class="text-right">{{ 'ApDocument_Total' | translateWF: 'Total' }}</th>
                  <th>{{ 'WithholdingRemittance_Certificate' | translateWF: 'Certificate' }}</th>
                  <th class="text-right">{{ 'Withholding_Retained' | translateWF: 'Withheld' }}</th>
                  <th>{{ 'WithholdingRemittance_Remitted' | translateWF: 'Remitted' }}</th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-rowData>
                @let row = getRow(rowData);
                <tr>
                  <td>
                    <!-- #4893 — the worksheet is reconciled against the month's paper invoices, which carry
                         only the fiscal number; lead with it. -->
                    <app-document-identity [documentNumber]="row.documentNumber" [fiscalNumber]="row.fiscalNumber" [clickable]="false" />
                  </td>
                  <td>{{ row.providerName || '—' }}</td>
                  <td>{{ row.postingDate | date: 'dd/MM/yyyy' }}</td>
                  <td class="text-right">{{ row.documentTotal | number: '1.2-2' }}</td>
                  <td>
                    @if (row.retentionStatus === 'None') {
                      <p-tag severity="danger" [value]="'WithholdingRemittance_NoCert' | translateWF: 'No certificate'" />
                    } @else {
                      <p-tag [severity]="retentionSeverity(row.retentionStatus)" [value]="row.retentionNumber || row.retentionStatus" />
                    }
                  </td>
                  <td class="text-right">{{ row.withheldAmount | number: '1.2-2' }}</td>
                  <td>
                    @if (row.remitted) {
                      <p-tag
                        severity="success"
                        [value]="('WithholdingRemittance_Remitted' | translateWF: 'Remitted') + (row.remittedDate ? ' · ' + (row.remittedDate | date: 'dd/MM/yyyy') : '')"
                      />
                    } @else if (row.withheldAmount > 0) {
                      <p-tag severity="secondary" [value]="'WithholdingRemittance_Pending' | translateWF: 'Pending'" />
                    } @else {
                      <span class="text-surface-400">—</span>
                    }
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="7">{{ 'WithholdingRemittance_NoDocs' | translateWF: 'No documents posted in this period.' }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        </p-panel>

        <!-- Charges SAR added on top of the withholdings (#5029). Only on a draft: once posted these are on
             a journal entry and in the bank balance, and the correction is a separate entry. -->
        @if (hasDraft()) {
          <p-panel [header]="'WithholdingRemittance_Charges' | translateWF: 'Charges from SAR'">
            <ng-template pTemplate="icons">
              <p-button
                [label]="'WithholdingRemittance_AddCharge' | translateWF: 'Add charge'"
                icon="pi pi-plus"
                severity="secondary"
                size="small"
                [text]="true"
                (click)="addCharge()"
              />
            </ng-template>
            <p-card>
              <p class="text-sm text-surface-500 mb-3">
                {{
                  'WithholdingRemittance_Charges_Hint'
                    | translateWF
                      : 'Late-filing penalties, surcharges or interest. These are not withholdings — they clear no liability, so
              each one posts to the account you choose and is excluded from the ISR and IVA totals.'
                }}
              </p>

              @if (charges.controls.length === 0) {
                <p class="text-sm text-surface-400">
                  {{
                    'WithholdingRemittance_NoCharges'
                      | translateWF
                        : 'No charges — SAR
              billed only the withholdings.'
                  }}
                </p>
              }

              @for (charge of charges.controls; track $index) {
                <div class="grid-12 mb-3 items-start">
                  <div class="span-12-5">
                    <label>{{ 'WithholdingRemittance_ChargeDescription' | translateWF: 'Description' }} <span class="text-red-500">*</span></label>
                    <input
                      type="text"
                      pInputText
                      class="w-full"
                      [formControl]="charge.controls.description"
                      [placeholder]="'WithholdingRemittance_ChargePlaceholder' | translateWF: 'e.g. Multa por presentación tardía'"
                    />
                    @if (charge.controls.description.touched && charge.controls.description.invalid) {
                      <small class="text-red-500">{{ 'Required' | translateWF: 'Required' }}</small>
                    }
                  </div>

                  <div class="span-12-4">
                    <app-financial-account-select
                      [referenceControl]="charge.controls.glAccountReference"
                      [numberControl]="charge.controls.glAccountNumber"
                      [nameControl]="charge.controls.accountName"
                      [label]="'WithholdingRemittance_ChargeAccount' | translateWF: 'GL account'"
                      [required]="true"
                      [postingModule]="PostingModule.AccountsPayable"
                      [placeholder]="'WithholdingRemittance_SelectAccount' | translateWF: 'Select account'"
                    />
                  </div>

                  <div class="span-8 md:span-2">
                    <label>{{ 'WithholdingRemittance_ChargeAmount' | translateWF: 'Amount' }} <span class="text-red-500">*</span></label>
                    <p-inputNumber
                      [formControl]="charge.controls.amount"
                      mode="decimal"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      [min]="0"
                      styleClass="w-full"
                      (onInput)="onChargeAmountChange()"
                      (onBlur)="onChargeAmountChange()"
                    />
                  </div>

                  <div class="span-4 md:span-1 flex items-end h-full">
                    <p-button icon="pi pi-trash" severity="danger" [text]="true" [ariaLabel]="'Remove' | translateWF: 'Remove'" (click)="removeCharge($index)" />
                  </div>
                </div>
              }

              @if (charges.controls.length > 0) {
                <div class="row-end-stretch">
                  <p-button
                    [label]="'WithholdingRemittance_SaveCharges' | translateWF: 'Save charges'"
                    icon="pi pi-save"
                    severity="secondary"
                    size="small"
                    (click)="saveCharges()"
                  />
                </div>
              }
            </p-card>
          </p-panel>
        }
      </div>

      <!-- RIGHT COLUMN -->
      <div class="span-12-12-4 col sticky-summary">
        <!-- Summary -->
        <p-panel [header]="'WithholdingRemittance_ToPay' | translateWF: 'To remit to SAR'">
          @if (worksheet(); as w) {
            <p-card>
              <div class="summary-rows">
                <div class="summary-rows__row">
                  <span>{{ 'WithholdingRemittance_Isr' | translateWF: 'ISR (DMR)' }}</span>
                  <span>{{ w.isrTotal | number: '1.2-2' }}</span>
                </div>
                <div class="summary-rows__row">
                  <!-- #4782 — display wording is IVA; DEI-352 stays, it is the real filing form name. The
                         translation key and the underlying isv* field names are unchanged (display-only rename). -->
                  <span>{{ 'WithholdingRemittance_Isv' | translateWF: 'IVA (DEI-352)' }}</span>
                  <span>{{ w.isvTotal | number: '1.2-2' }}</span>
                </div>
                <div class="summary-rows__row summary-rows__row--total">
                  <span>{{ 'WithholdingRemittance_Total' | translateWF: 'Total to remit' }}</span>
                  <span>{{ w.totalToRemit | number: '1.2-2' }}</span>
                </div>
                <!-- Shown only when there are charges, so the ordinary remittance keeps reading as one number
                       and the split appears exactly when it starts to matter. -->
                @if (chargesTotal() > 0) {
                  <div class="summary-rows__row">
                    <span>{{ 'WithholdingRemittance_ChargesTotal' | translateWF: 'Charges from SAR' }}</span>
                    <span>{{ chargesTotal() | number: '1.2-2' }}</span>
                  </div>
                  <div class="summary-rows__row summary-rows__row--total">
                    <span>{{ 'WithholdingRemittance_TotalPaid' | translateWF: 'Total to pay' }}</span>
                    <span>{{ totalPaid() | number: '1.2-2' }}</span>
                  </div>
                }
              </div>
            </p-card>
          }
        </p-panel>

        <!-- What has already been filed for this period (#5029). A month can be remitted more than once: a
             transfer goes out, an invoice surfaces later, and the top-up is filed against the same period.
             Shown before the action panel because it is the context for whatever is filed next. -->
        @if (remittances().length > 0) {
          <p-panel [header]="'WithholdingRemittance_Filed' | translateWF: 'This is what you remitted'">
            <p-card>
              @for (filing of remittances(); track filing.id) {
                <button type="button" class="filing-row" (click)="openRemittance(filing)">
                  <div class="filing-row__head">
                    <span class="filing-row__number">{{ filing.number }}</span>
                    <p-tag [severity]="remittanceSeverity(filing.status)" [value]="remittanceStatusLabel(filing.status)" />
                  </div>
                  <div class="filing-row__meta">
                    <span>
                      @if (filing.postedDate) {
                        {{ filing.postedDate | date: 'dd/MM/yyyy' }}
                      } @else {
                        {{ 'WithholdingRemittance_NotPosted' | translateWF: 'Not posted yet' }}
                      }
                      · {{ filing.lineCount }} {{ 'WithholdingRemittance_Retentions' | translateWF: 'retentions' }}
                    </span>
                    <span class="filing-row__amount">{{ filing.totalPaid | number: '1.2-2' }}</span>
                  </div>
                  @if (filing.chargesAmount > 0) {
                    <div class="filing-row__meta">
                      <span class="text-surface-400">{{
                        'WithholdingRemittance_IncludesCharges'
                          | translateWF
                            : 'includes
                  charges'
                      }}</span>
                      <span class="text-surface-400">{{ filing.chargesAmount | number: '1.2-2' }}</span>
                    </div>
                  }
                </button>
              }

              @if (postedRemittances().length > 1) {
                <!-- Only once a period has been filed more than once: with a single filing this repeats the row
                   above it, and a number that says nothing new is noise. -->
                <div class="summary-rows mt-2">
                  <div class="summary-rows__row summary-rows__row--total">
                    <span>{{ 'WithholdingRemittance_PeriodTotal' | translateWF: 'Remitted this period' }}</span>
                    <span>{{ totalRemittedForPeriod() | number: '1.2-2' }}</span>
                  </div>
                </div>
              }
            </p-card>
          </p-panel>
        }

        <!-- Action -->
        <p-panel [header]="'WithholdingRemittance_Action' | translateWF: 'Remit'">
          <p-card>
            @if (periodClosed()) {
              <p-message severity="success" styleClass="w-full" [text]="'WithholdingRemittance_Done' | translateWF: 'This period has already been remitted to SAR.'" />
            } @else if (awaitingMoreRetentions()) {
              <!-- Filed, and open to a top-up (#5029) — but nothing new has been certified yet. Said out loud,
                   because the officer's next move depends on knowing the month can be filed again: the old
                   "Nothing to remit" read as "this period is closed" and hid the whole supplementary route. -->
              <p-message
                severity="info"
                styleClass="w-full"
                [text]="
                  'WithholdingRemittance_AllRemitted'
                    | translateWF: 'Everything certified for this period has been remitted. Post a certificate for the month and you can file a top-up here.'
                "
              />
            } @else if (totalToRemit() <= 0 && !hasDraft()) {
              <p class="text-sm text-surface-500">{{ 'WithholdingRemittance_Nothing' | translateWF: 'Nothing to remit for this period.' }}</p>
            } @else if (!hasDraft()) {
              <p class="text-sm text-surface-500 mb-3">
                @if (hasFiledThisPeriod()) {
                  <!-- A top-up covers only what the earlier filings did not take — the draft is seeded from
                       unremitted retentions alone, so it can never sweep one twice. -->
                  {{
                    'WithholdingRemittance_PrepareAnotherHint'
                      | translateWF
                        : 'This period has already been filed. A new remittance covers only the retentions the
                earlier filings did not take.'
                  }}
                } @else {
                  {{
                    'WithholdingRemittance_PrepareHint'
                      | translateWF
                        : 'Prepare the remittance to review the batch, then
                pick a bank and post.'
                  }}
                }
              </p>
              <p-button
                [label]="
                  hasFiledThisPeriod()
                    ? ('WithholdingRemittance_PrepareAnother' | translateWF: 'Prepare another remittance')
                    : ('WithholdingRemittance_Prepare' | translateWF: 'Prepare remittance')
                "
                icon="pi pi-file-check"
                styleClass="w-full"
                (click)="prepare()"
              />
            } @else {
              <!-- The draft may predate a Constancia posted since it was prepared; this pulls those in without
                 opening a second draft, which the API refuses anyway. -->
              <div class="row-between-center">
                <span class="text-sm text-surface-500">{{ draft()?.number }}</span>
                <p-button
                  [label]="'WithholdingRemittance_Refresh' | translateWF: 'Refresh'"
                  icon="pi pi-refresh"
                  severity="secondary"
                  size="small"
                  [text]="true"
                  (click)="refreshDraft()"
                />
              </div>
              <div class="form-box">
                <label>{{ 'WithholdingRemittance_BankAccount' | translateWF: 'Pay from bank account' }} <span class="text-red-500">*</span></label>
                <p-select
                  [formControl]="bankControl"
                  [options]="bankOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  appendTo="body"
                  styleClass="w-full"
                  [placeholder]="'WithholdingRemittance_SelectBank' | translateWF: 'Select a bank account'"
                />
              </div>
              <!-- The bank converts (#5029): the filing owes Lempiras and the account holds another money. The
                 rate is the bank's, so it is asked for rather than defaulted from the day's table, and what
                 will actually leave the account is shown as it is typed. -->
              @if (converts()) {
                <div class="form-box">
                  <label>
                    {{ 'WithholdingRemittance_WireRate' | translateWF: 'Exchange rate from the bank' }}
                    &middot; {{ currencyLabel(quoteCurrency()) }}
                    {{
                      'WithholdingRemittance_PerUsd'
                        | translateWF
                          : 'per
                  USD'
                    }}
                    <span class="text-red-500">*</span>
                  </label>
                  <p-inputNumber
                    [formControl]="rateControl"
                    mode="decimal"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="rateDecimals"
                    styleClass="w-full"
                    [inputStyleClass]="rateOutOfBounds() ? 'w-full ng-invalid ng-dirty' : 'w-full'"
                    [placeholder]="'WithholdingRemittance_WireRate_Placeholder' | translateWF: 'Rate the bank gave you'"
                  />
                  @if (rateOutOfBounds()) {
                    <small class="text-red-500">
                      {{
                        'WithholdingRemittance_RateOutOfBounds_Detail'
                          | translateWF
                            : 'That rate is outside the range this
                  currency trades at.'
                      }}
                      @if (rateBoundsHint(); as bounds) {
                        &middot; {{ bounds }}
                      }
                    </small>
                  } @else {
                    <small class="text-surface-500">
                      {{ 'WithholdingRemittance_WireRate_Hint' | translateWF: 'SAR is paid' }}
                      {{ totalPaid() | number: '1.2-2' }} {{ currencyLabel(remittanceCurrency()) }} &middot;
                      @if (cashOut() > 0) {
                        {{ 'WithholdingRemittance_WillLeave' | translateWF: 'leaving the account:' }}
                        <strong>{{ cashOut() | number: '1.2-2' }} {{ currencyLabel(fundingCurrency()) }}</strong>
                      } @else {
                        {{
                          'WithholdingRemittance_WillLeave_Pending'
                            | translateWF
                              : 'enter the rate to see what leaves the
                  account'
                        }}
                      }
                    </small>
                  }
                </div>
              }
              <!-- Which week's cash this comes from (#4796). A tag, not a gate: the remittance posts without it,
                 and it only decides whose usage the money shows up in. -->
              <div class="form-box">
                <label>{{ 'WithholdingRemittance_CashRequest' | translateWF: 'Cash request' }}</label>
                <p-select
                  [formControl]="cashRequestControl"
                  [options]="cashRequestOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  [showClear]="true"
                  appendTo="body"
                  styleClass="w-full"
                  [placeholder]="'WithholdingRemittance_SelectCashRequest' | translateWF: 'Not planned in a cash request'"
                />
                @if (plannedCashRequestHint(); as hint) {
                  <small class="text-surface-500">{{ hint }}</small>
                }
              </div>
              <div class="form-box">
                <label>{{ 'WithholdingRemittance_BankingRef' | translateWF: 'Bank reference #' }}</label>
                <input type="text" pInputText class="w-full" [formControl]="bankingRefControl" />
              </div>
              <div class="form-box">
                <label>{{ 'ApDocument_Section_Notes' | translateWF: 'Notes' }}</label>
                <textarea pTextarea [formControl]="notesControl" rows="2" class="w-full"></textarea>
              </div>
              <p-button [label]="'WithholdingRemittance_Remit' | translateWF: 'Remit to SAR'" icon="pi pi-check" severity="success" styleClass="w-full" (click)="remit()" />
            }
          </p-card>
        </p-panel>
      </div>
    </div>
  </div>

  <!-- A filing already made, read-only (#5029). Opened from the list to answer the question that actually
       drives a second remittance: which retentions did the earlier one already cover? -->
  <p-dialog
    [visible]="viewerOpen()"
    (visibleChange)="$event ? null : closeViewer()"
    [modal]="true"
    [draggable]="false"
    [dismissableMask]="true"
    styleClass="w-full max-w-4xl"
    [header]="viewing()?.number ?? ('WithholdingRemittance_Title' | translateWF: 'Withholding Remittance')"
  >
    @if (viewing(); as filing) {
      <div class="grid-12 mb-4">
        <div class="span-6-3">
          <label class="text-surface-500 text-sm">{{ 'WithholdingRemittance_Status' | translateWF: 'Status' }}</label>
          <div><p-tag [severity]="remittanceSeverity(filing.status)" [value]="remittanceStatusLabel(filing.status)" /></div>
        </div>
        <div class="span-6-3">
          <label class="text-surface-500 text-sm">{{ 'WithholdingRemittance_JournalEntry' | translateWF: 'Journal entry' }}</label>
          <div>{{ filing.journalEntryDocumentNumber || '—' }}</div>
        </div>
        <div class="span-6-3">
          <label class="text-surface-500 text-sm">{{ 'WithholdingRemittance_BankingRef' | translateWF: 'Bank reference #' }}</label>
          <div>{{ filing.bankingReference || '—' }}</div>
        </div>
        <div class="span-6-3">
          <label class="text-surface-500 text-sm">{{ 'WithholdingRemittance_TotalPaid' | translateWF: 'Total to pay' }}</label>
          <div class="font-semibold">{{ filing.totalPaid | number: '1.2-2' }} {{ currencyLabel(filing.currency) }}</div>
        </div>
      </div>

      <p-table [value]="filing.lines" styleClass="p-datatable-sm" [scrollable]="true" scrollHeight="320px">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ 'WithholdingRemittance_Certificate' | translateWF: 'Certificate' }}</th>
            <th>{{ 'ApDocument_Provider' | translateWF: 'Provider' }}</th>
            <th>{{ 'ApDocument_Number' | translateWF: 'Document' }}</th>
            <th class="text-right">{{ 'WithholdingRemittance_Withheld' | translateWF: 'Withheld' }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-line>
          <tr>
            <td>{{ line.retentionNumber }}</td>
            <td>{{ line.providerName }}</td>
            <td>{{ line.invoiceNumber || '—' }}</td>
            <td class="text-right">{{ line.amount | number: '1.2-2' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="4">{{ 'WithholdingRemittance_NoLines' | translateWF: 'No retentions on this remittance.' }}</td>
          </tr>
        </ng-template>
      </p-table>

      @if (filing.charges.length > 0) {
        <h4 class="mt-4 mb-2">{{ 'WithholdingRemittance_Charges' | translateWF: 'Charges from SAR' }}</h4>
        <p-table [value]="filing.charges" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'WithholdingRemittance_ChargeDescription' | translateWF: 'Description' }}</th>
              <th>{{ 'WithholdingRemittance_ChargeAccount' | translateWF: 'GL account' }}</th>
              <th class="text-right">{{ 'WithholdingRemittance_ChargeAmount' | translateWF: 'Amount' }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-charge>
            <tr>
              <td>{{ charge.description }}</td>
              <td>{{ charge.glAccountNumber }} · {{ charge.accountName }}</td>
              <td class="text-right">{{ charge.amount | number: '1.2-2' }}</td>
            </tr>
          </ng-template>
        </p-table>
      }

      <div class="summary-rows mt-4">
        <div class="summary-rows__row">
          <span>{{ 'WithholdingRemittance_Total' | translateWF: 'Total to remit' }}</span>
          <span>{{ filing.amount | number: '1.2-2' }}</span>
        </div>
        @if (filing.chargesAmount > 0) {
          <div class="summary-rows__row">
            <span>{{ 'WithholdingRemittance_ChargesTotal' | translateWF: 'Charges from SAR' }}</span>
            <span>{{ filing.chargesAmount | number: '1.2-2' }}</span>
          </div>
        }
        <div class="summary-rows__row summary-rows__row--total">
          <span>{{ 'WithholdingRemittance_TotalPaid' | translateWF: 'Total to pay' }}</span>
          <span>{{ filing.totalPaid | number: '1.2-2' }}</span>
        </div>
      </div>
    }
  </p-dialog>
</main>
